import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ReportDataProvider } from './context/ReportDataContext';
import { DisputeTrayProvider } from './context/DisputeTrayContext';
import { PortalProvider, usePortal, PORTAL_TYPES } from './context/PortalContext';
import { SupportContextProvider, useSupportContext } from './context/SupportContext';
import { ThemeProvider } from './context/ThemeContext';
import { I18nProvider } from './context/I18nContext';
import { OnboardingProvider, useOnboarding } from './context/OnboardingContext';
import { EntitlementProvider } from './context/EntitlementContext';
import { Toaster } from './components/ui/sonner';

// Layout (original)
import AppLayout from './components/AppLayout';

// Portal Layouts (Phase 6)
import StaffPortalLayout from './layouts/StaffPortalLayout';
import SelfServePortalLayout from './layouts/SelfServePortalLayout';
import ManagedClientPortalLayout from './layouts/ManagedClientPortalLayout';

// Platform Owner Layout (Phase 8)
import PlatformOwnerLayout from './layouts/PlatformOwnerLayout';

// Auth Pages
import Login from './pages/auth/Login';
import ChooseAccountPage from './pages/auth/ChooseAccountPage';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import ConfirmLoginEmail from './pages/auth/ConfirmLoginEmail';
import VerifyEmail from './pages/auth/VerifyEmail';
import AuthSignupRoute from './pages/auth/AuthSignupRoute';
import AuthCallback from './pages/auth/AuthCallback';
import SelfServeSignup from './pages/auth/SelfServeSignup';
import AgencySignup from './pages/auth/AgencySignup';

// Onboarding Flow (Auto Starter)
import OnboardingFlow from './pages/onboarding/OnboardingFlow';

// Main Pages
import Dashboard from './pages/Dashboard';
import ImportCreditReport from './pages/credit-report/ImportCreditReport';
import CreditReport from './pages/credit-report/CreditReport';
import DisputeCenter from './pages/disputes/DisputeCenter';
import DisputesDashboard from './pages/disputes/DisputesDashboard';
import DisputeWizardPage from './pages/disputes/DisputeWizardPage';
import SpecialistProWorkspace from './pages/disputes/SpecialistProWorkspace';
import RoundDetailPage from './pages/disputes/RoundDetailPage';
import PackageDetailPage from './pages/disputes/PackageDetailPage';
import DisputePathRecommendationScreen from './pages/disputes/DisputePathRecommendationScreen';
import FactualDisputeSelectionScreen from './pages/disputes/FactualDisputeSelectionScreen';
import Metro2DisputeSelectionScreen from './pages/disputes/Metro2DisputeSelectionScreen';
import Metro2TechnicalWizard from './pages/disputes/Metro2TechnicalWizard';
import ReviewFinalizeStep from './pages/disputes/ReviewFinalizeStep';
import AutoStarterDisputeFlow from './pages/disputes/AutoStarterDisputeFlow';
import AIAssistant from './pages/AIAssistant';
import Billing from './pages/Billing';
import Academy from './pages/Academy';

// New Feature Pages
import FundingReadiness from './pages/funding/FundingReadiness';
import FundGeniusHQLayout from './layouts/funding/FundGeniusHQLayout';
import FundGeniusHQDashboard from './pages/funding/FundGeniusHQDashboard';
import FundingClientsPage from './pages/funding/FundingClientsPage';
import {
  FundingProfilesPage,
  FundingReadinessPage,
  FundingDocumentsPage,
  FundingBankMatchPage,
  FundingSequencePage,
  FundingSubmissionsPage,
  FundingPipelinePage,
  FundingCommissionsPage,
  FundingReportsPage,
  FundingLendersPage,
  FundingLenderDetailPage,
  FundingAIAdvisorPage,
  FundingSettingsPage,
} from './pages/funding/FundingModulePages';
import ScoreSimulator from './pages/credit-builder/ScoreSimulator';
import UtilizationManager from './pages/credit-builder/UtilizationManager';
import PaymentPlanner from './pages/credit-builder/PaymentPlanner';
import CLIPlanner from './pages/credit-builder/CLIPlanner';
import TradelineStrategy from './pages/credit-builder/TradelineStrategy';
import Settings from './pages/settings/Settings';
import ClientCases from './pages/cases/ClientCases';

// Settings Pages (Phase 9.1)
import AppearanceSettings from './pages/settings/AppearanceSettings';
import TeamActivityPage from './pages/settings/TeamActivityPage';
import CustomCodeSettings from './pages/settings/CustomCodeSettings';
import SettingsHub from './pages/settings/SettingsHub';

// Self-Serve Portal Pages
import SelfServeDocumentsPage from './pages/portal/selfserve/SelfServeDocumentsPage';
import ReportAnalysisPreview from './pages/portal/selfserve/ReportAnalysisPreview';
import ReportAnalysisPrint from './pages/portal/selfserve/ReportAnalysisPrint';

// My Documents Page (Enhanced with QR scanning, crop, approval workflow)
import MyDocumentsPage from './pages/documents/MyDocumentsPage';
import MobileScanPage from './pages/documents/MobileScanPage';
import TriggerConnectorProofPage from './pages/dev/TriggerConnectorProofPage';

// Admin Pages (Phase 13 - Admin Sidebar Control)
import AdminLayout from './layouts/AdminLayout';
import AdminOverviewPage from './pages/admin/AdminOverviewPage';
import ParserTraining from './pages/admin/ParserTraining';
import AdminDashboard from './pages/admin/AdminDashboard';
import KnowledgeBase from './pages/admin/KnowledgeBase';
import PromptManagement from './pages/admin/PromptManagement';
import LetterTraining from './pages/admin/LetterTraining';
import WorkflowRulesPage from './pages/admin/WorkflowRulesPage';
import MethodLibraryPage from './pages/admin/MethodLibraryPage';
import ExtractionRulesPage from './pages/admin/ExtractionRulesPage';
import RoutingRulesPage from './pages/admin/RoutingRulesPage';
import StrategyMappingPage from './pages/admin/StrategyMappingPage';
import RuleTestingPage from './pages/admin/RuleTestingPage';
import TemplateManagerPage from './pages/admin/TemplateManagerPage';
import ReviewQueuePage from './pages/admin/ReviewQueuePage';
import SandboxTesterPage from './pages/admin/SandboxTesterPage';
import SystemStatusPage from './pages/admin/SystemStatusPage';
import PermissionsPage from './pages/admin/PermissionsPage';
import UserLoginAccessPage from './pages/admin/UserLoginAccessPage';
import TrainingIntakePage from './pages/admin/TrainingIntakePage';
import AuditLogPage from './pages/admin/AuditLogPage';
import TwoFactorSettingsPage from './pages/admin/TwoFactorSettingsPage';
import BlueprintAdmin from './pages/admin/BlueprintAdmin';
import FiveSClassificationPage from './pages/admin/FiveSClassificationPage';
// New Admin Architecture Pages
import RulesMethodsPage from './pages/admin/RulesMethodsPage';
import TemplateManagerFullPage from './pages/admin/TemplateManagerFullPage';
import RuleTestingPageV2 from './pages/admin/RuleTestingPageV2';
// Phase 5: Decision and Letter Training Management
import DecisionTrainingPage from './pages/admin/DecisionTrainingPage';
import LetterTrainingManagement from './pages/admin/LetterTrainingManagement';
// Metro 2 Intelligence Admin Console
import Metro2IntelligencePage from './pages/admin/Metro2IntelligencePage';
import DataImpulseLocationCatalogPage from './pages/admin/DataImpulseLocationCatalogPage';
import DataImpulseProviderPage from './pages/admin/DataImpulseProviderPage';
import Proxy911ProviderPage from './pages/admin/Proxy911ProviderPage';
import QaProxyPolicyPage from './pages/admin/QaProxyPolicyPage';
import QaWebsiteAccessPage from './pages/admin/QaWebsiteAccessPage';
import QaDiagnosticsPage from './pages/public/QaDiagnosticsPage';

// AI Operations Pages (Phase 1 - Foundation)
import AIOperationsOverview from './pages/admin/ai/AIOperationsOverview';
import AIAssistantPage from './pages/admin/ai/AIAssistantPage';
import DeveloperAgentPage from './pages/admin/ai/DeveloperAgentPage';
import AICommandCenter from './pages/admin/ai/AICommandCenter';
import AITrainingPage from './pages/admin/ai/AITrainingPage';
import AIUsagePage from './pages/admin/ai/AIUsagePage';
import AITicketsPage from './pages/admin/ai/AITicketsPage';
import PublicLandingPage from './pages/public/PublicLandingPage';
import FundGeniusPublicLandingPage from './pages/public/FundGeniusPublicLandingPage';
import FundingIntakePortalPage from './pages/funding/FundingIntakePortalPage';
import { getPortalFromHostname, PORTAL_SURFACES, isPlatformConsoleRole, getDashboardForRoleOnPortal, getHostnamePortal, HOSTNAME_PORTALS } from './utils/hostnamePortal';
import {
  agencyOwnerNeedsEmailVerification,
  getDashboardForUser,
  isAgencyWorkspaceUser,
  isAgencyUser,
  isClientPortalUser,
  isPlatformAdmin,
  getClientPortalDashboardPath,
  AGENCY_DASHBOARD_PATH,
  FUNDGENIUS_APP_CLIENTS_PATH,
} from './utils/authUtils';
import { isScopedSelfRepairSession } from './utils/sessionScope';
import {
  getPortalLandingRoute,
  resolveAuthenticatedRootRoute,
} from './utils/portalLandingRoute';
import {
  isPlatformDashboardPath,
  logPortalRedirectRejected,
  shouldRejectStaleFundGeniusRedirect,
} from './utils/backToPlatformNavigation';
import HostnameRouteGate from './components/HostnameRouteGate';
import { resolveAgencyRouteAccess, isCanonicalSelfRepairScope } from './utils/agencyRouteGuard';

// Collection Letter Markup (Phase 14)
import CollectionLetterMarkupPage from './pages/collection-markup/CollectionLetterMarkupPage';

// Academy Pages - Educational Blueprints
import AuthorizedUserBlueprint from './pages/academy/AuthorizedUserBlueprint';
import CreditUnionBlueprint from './pages/academy/CreditUnionBlueprint';
import RentReportingBlueprint from './pages/academy/RentReportingBlueprint';
// NOTE: SecuredLoanBlueprint removed - replaced by TradelineStrategy under Credit Builder

// Placeholder Blueprint Page for Coming Soon pages
const PlaceholderBlueprintPage = ({ type }) => {
  const titles = {
  };
  const descriptions = {
  };
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 flex items-center justify-center">
          <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-[#1a1f1c] mb-3">{titles[type]}</h2>
        <p className="text-[#6b7672] mb-6">{descriptions[type]}</p>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Coming Soon
        </div>
      </div>
    </div>
  );
};

// Dispute Wizard (New Feature)
import { DisputeWizardLanding, BankruptcyReviewFlow, AlternateBureauFlow, SystemStatesDemo, ItemSelectionPage, LetterPreviewPage, FinalPackagePreviewPage } from './pages/dispute-wizard';
import DeliveryFlowStep7 from './pages/dispute-wizard/DeliveryFlowStep7';
import DeliveryFlowStep8 from './pages/dispute-wizard/DeliveryFlowStep8';
import Step6FinalPreviewRedirect from './pages/dispute-wizard/Step6FinalPreviewRedirect';

// Portal Pages (Phase 6)
import StaffDashboard from './pages/portal/staff/StaffDashboard';
import ManagedClientDashboard from './pages/portal/client/ManagedClientDashboard';
import PortalPlaceholder from './components/portal/PortalPlaceholder';
import PlatformUsersPage from './pages/platform/PlatformUsersPage';

// Portal Pages (Phase 7) - Real Data
import StaffCasesPage from './pages/portal/staff/StaffCasesPage';
import StaffDisputeQueuePage from './pages/portal/staff/StaffDisputeQueuePage';
import StaffDocumentRequestsPage from './pages/portal/staff/StaffDocumentRequestsPage';
import StaffUploadsQueuePage from './pages/portal/staff/StaffUploadsQueuePage';
import StaffTasksQueuePage from './pages/portal/staff/StaffTasksQueuePage';
import StaffActivityPage from './pages/portal/staff/StaffActivityPage';
import StaffCaseDetailPage from './pages/portal/staff/StaffCaseDetailPage';
import ClientsUsersPage from './pages/clients/ClientsUsersPage';
import SelfServeDashboard from './pages/portal/selfserve/SelfServeDashboard';
import ManagedClientDisputesPage from './pages/portal/client/ManagedClientDisputesPage';
import ManagedClientDocumentsPage from './pages/portal/client/ManagedClientDocumentsPage';
import ManagedClientMessagesPage from './pages/portal/client/ManagedClientMessagesPage';

// Platform Owner Pages (Phase 8)
import PlatformDashboard from './pages/platform/PlatformDashboard';
import AgencyListPage from './pages/platform/AgencyListPage';
import AgencyDetailPage from './pages/platform/AgencyDetailPage';
import AgencySettingsPage from './pages/platform/AgencySettingsPage';
import SupportActivityPage from './pages/platform/SupportActivityPage';
import PlatformPlansPage from './pages/platform/PlatformPlansPage';
import PlatformSettingsPage from './pages/platform/PlatformSettingsPage';

// Platform Settings Workspace
import PlatformSettingsLayout from './layouts/PlatformSettingsLayout';
import PlatformSettingsOverview from './pages/platform/settings/PlatformSettingsOverview';
import SettingsPlaceholder from './pages/platform/settings/SettingsPlaceholder';
import AuthenticationSettingsPage from './pages/platform/settings/AuthenticationSettingsPage';
import PlatformProfileSettings from './pages/platform/settings/PlatformProfileSettings';
import GoogleAddressSettings from './pages/platform/settings/integrations/GoogleAddressSettings';
import PlatformMailgunSettings from './pages/platform/settings/integrations/PlatformMailgunSettings';
import PlatformWhopSettings from './pages/platform/settings/integrations/PlatformWhopSettings';
import PlatformOpenAISettings from './pages/platform/settings/integrations/PlatformOpenAISettings';
import PlatformPostGridSettings from './pages/platform/settings/integrations/PlatformPostGridSettings';
import SystemAutomationsPage from './pages/platform/settings/communication/SystemAutomationsPage';
import PlatformDomainsSettings from './pages/platform/settings/communication/PlatformDomainsSettings';

// Platform Automations (System Workflows)
import PlatformAutomationsPage from './pages/platform/automations/PlatformAutomationsPage';
import PlatformWorkflowBuilderPage from './pages/platform/automations/PlatformWorkflowBuilderPage';

// Platform Contacts (CRM)
import PlatformContactsPage from './pages/platform/contacts/PlatformContactsPage';
import PlatformContactWorkspacePage from './pages/platform/contacts/PlatformContactWorkspacePage';

// Platform Unified Inbox
import PlatformUnifiedInboxPage from './pages/platform/inbox/PlatformUnifiedInboxPage';

// Platform Opportunities (CRM Pipelines)
import PlatformOpportunitiesPage from './pages/platform/opportunities/PlatformOpportunitiesPage';
import PlatformPipelineEditPage from './pages/platform/opportunities/PlatformPipelineEditPage';
import PlatformTasksPage from './pages/platform/tasks/PlatformTasksPage';
import PlatformTaskBulkActionsPage from './pages/platform/tasks/PlatformTaskBulkActionsPage';
import AgencyTasksPage from './pages/agency/tasks/AgencyTasksPage';
import AgencyTaskBulkActionsPage from './pages/agency/tasks/AgencyTaskBulkActionsPage';

// Platform Data & System Settings
import PlatformObjectsPage from './pages/platform/settings/data/PlatformObjectsPage';
import PlatformCustomFieldsPage from './pages/platform/settings/data/PlatformCustomFieldsPage';
import PlatformCustomValuesPage from './pages/platform/settings/data/PlatformCustomValuesPage';
import PlatformManageScoringPage from './pages/platform/settings/data/PlatformManageScoringPage';
import PlatformTagsPage from './pages/platform/settings/data/PlatformTagsPage';
import PlatformExternalTrackingPage from './pages/platform/settings/data/PlatformExternalTrackingPage';
import PlatformIntegrationsHubPage from './pages/platform/settings/data/PlatformIntegrationsHubPage';
import PlatformPrivateIntegrationsPage from './pages/platform/settings/data/PlatformPrivateIntegrationsPage';

// Agency Workspace Pages (Agency-Scoped Context)
import AgencyWorkspaceLayout from './layouts/AgencyWorkspaceLayout';
import AgencyWorkspaceDashboard from './pages/agency/AgencyWorkspaceDashboard';
import AgencyWorkspaceSettingsPage from './pages/agency/AgencyWorkspaceSettingsPage';
import AgencyEmailServicesPage from './pages/agency/AgencyEmailServicesPage';
import ClientBillingPage from './pages/agency/ClientBillingPage';
import AutomationsPage from './pages/agency/automations/AutomationsPage';
import WorkflowBuilderPage from './pages/agency/automations/WorkflowBuilderPage';
import UnifiedInboxPage from './pages/agency/inbox/UnifiedInboxPage';
import ContactWorkspacePage from './pages/agency/contacts/ContactWorkspacePage';
import AgencyContactsPage from './pages/agency/contacts/AgencyContactsPage';

// Agency Opportunities
import AgencyOpportunitiesPage from './pages/agency/opportunities/AgencyOpportunitiesPage';

// Global Components
import ConsistencyDebugPanel from './components/ConsistencyDebugPanel';
import SupportWidget from './components/support/SupportWidget';
import { SupportWidgetProvider } from './context/SupportWidgetContext';
import { DisputeTray } from './components/disputes/DisputeTray';
import { BulkDisputeWizard } from './components/disputes/BulkDisputeWizard';

// Protected Route Component with Portal Support + Support Mode Awareness
const ProtectedRoute = ({ children, requiredPortal, allowStaffFallthrough }) => {
  const { user, loading, sessionReady, authState, awaitingAccountSelection } = useAuth();
  const { portalType, loading: portalLoading } = usePortal();
  const { isInSupportMode, contextReady } = useSupportContext();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-bootstrap-loading">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Awaiting account selection must never mount Self Repair or Agency shells.
  if (
    awaitingAccountSelection
    || authState === 'account_selection_required'
    || user?.requires_account_selection
    || user?.pending_account_selection
  ) {
    return <Navigate to="/choose-account" replace />;
  }

  if (!sessionReady || portalLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-bootstrap-loading">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // CRITICAL: Wait for SupportContext to finish loading before making redirect decisions
  // This prevents redirect loops during initial context hydration
  if (!contextReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Scoped self-repair must never remain on agency/staff shells.
  if (isScopedSelfRepairSession(user)) {
    if (location.pathname.startsWith('/agency') || location.pathname.startsWith('/staff')) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  const platformConsoleRole = isPlatformConsoleRole(user.role);
  const agencyWorkspaceUser = isAgencyWorkspaceUser(user) && !isScopedSelfRepairSession(user);

  if (agencyWorkspaceUser) {
    if (agencyOwnerNeedsEmailVerification(user) && !location.pathname.startsWith('/verify-email')) {
      return (
        <Navigate
          to="/verify-email"
          state={{
            email: user.email,
            role: 'agency_owner',
            redirectTo: AGENCY_DASHBOARD_PATH,
            agencyId: user.agency_id,
          }}
          replace
        />
      );
    }
    if (!location.pathname.startsWith('/agency') && location.pathname !== '/verify-email') {
      return <Navigate to={AGENCY_DASHBOARD_PATH} replace />;
    }
    return children;
  }

  if (platformConsoleRole) {
    if (
      (location.pathname.startsWith('/staff') || location.pathname.startsWith('/agency')) &&
      !isInSupportMode
    ) {
      return <Navigate to="/platform/dashboard" replace />;
    }
    return children;
  }

  // Platform owners and super admins can access ANY portal without restrictions
  // (Only when NOT in support mode - support mode is handled above)
  if (user.role === 'platform_owner' || user.role === 'super_admin') {
    return children;
  }

  // NORMAL MODE: Standard portal access checks
  // If a specific portal is required, redirect if user is in wrong portal
  if (requiredPortal && portalType !== requiredPortal) {
    // Redirect to appropriate portal
    if (portalType === PORTAL_TYPES.STAFF) {
      return <Navigate to="/staff/dashboard" replace />;
    } else if (portalType === PORTAL_TYPES.MANAGED_CLIENT) {
      return <Navigate to="/client/dashboard" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Auto-redirect staff users who access self-serve routes to staff portal
  // (Unless explicitly allowed to fall through for shared pages like credit report)
  if (!allowStaffFallthrough && portalType === PORTAL_TYPES.STAFF && !platformConsoleRole && !location.pathname.startsWith('/staff')) {
    // Allow certain shared routes (credit report, admin, settings, disputes, dispute-wizard)
    const sharedPaths = ['/credit-report', '/admin', '/settings', '/disputes', '/dispute-wizard'];
    const isSharedPath = sharedPaths.some(p => location.pathname.startsWith(p));
    if (!isSharedPath) {
      return <Navigate to="/staff/dashboard" replace />;
    }
  }

  // Auto-redirect managed clients who access self-serve routes  
  // Allow dispute-related paths since they are shared functionality
  if (portalType === PORTAL_TYPES.MANAGED_CLIENT && !location.pathname.startsWith('/client')) {
    const sharedPaths = ['/credit-report', '/disputes', '/dispute-wizard', '/settings'];
    const isSharedPath = sharedPaths.some(p => location.pathname.startsWith(p));
    if (!isSharedPath) {
      return <Navigate to="/client/dashboard" replace />;
    }
  }

  return children;
};

// Auto Starter Onboarding Protected Route - ONLY for self-serve users
// Uses OnboardingContext for backend-driven state management
const AutoStarterOnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { portalType, loading: portalLoading } = usePortal();
  const { state: onboardingState, loading: onboardingLoading, isAutoStarter } = useOnboarding();
  const location = useLocation();

  if (loading || portalLoading || onboardingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Only allow self-serve users (Auto Starter) to access onboarding
  // Redirect staff, managed clients, and platform console roles to their dashboards
  if (isPlatformConsoleRole(user.role)) {
    return <Navigate to="/platform/dashboard" replace />;
  }

  if (portalType === PORTAL_TYPES.STAFF && !isAgencyWorkspaceUser(user)) {
    return <Navigate to={getDashboardForUser(user)} replace />;
  }
  
  if (portalType === PORTAL_TYPES.MANAGED_CLIENT) {
    return <Navigate to="/client/dashboard" replace />;
  }

  // If user has already completed onboarding and has imported a report, go to dashboard
  if (onboardingState?.onboarding_completed && onboardingState?.has_imported_report) {
    return <Navigate to="/dashboard" replace />;
  }

  // Only self-serve / auto starter users proceed
  return children;
};

// Platform Owner Protected Route - For platform admin console
const PlatformOwnerProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { supportContext, isInSupportMode, contextReady } = useSupportContext();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Wait for support context before support-mode redirects (prevents /dashboard ↔ /platform loop)
  if (isPlatformConsoleRole(user.role) && !contextReady && !user.is_support_mode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // SUPPORT MODE: When viewing as a client/agency, redirect away from unrelated
  // platform console routes to the correct support surface.
  if (isInSupportMode) {
    const effectivePortal = supportContext?.effective_portal_type;
    const onFundGeniusHq = location.pathname.startsWith('/platform/fundgenius-hq');
    const agencyId = supportContext?.effective_agency_id;
    const fundGeniusClientsPath = agencyId
      ? `/platform/fundgenius-hq/clients?agency_id=${encodeURIComponent(agencyId)}`
      : '/platform/fundgenius-hq/clients';

    if (effectivePortal === 'self_serve_portal' || effectivePortal === 'self_repair') {
      return <Navigate to={supportContext?.redirect_path || '/dashboard'} replace />;
    }
    if (effectivePortal === 'managed_client_portal') {
      return <Navigate to="/client/dashboard" replace />;
    }
    if (effectivePortal === 'fundgenius_hq' || effectivePortal === 'fundgenius') {
      // Stay on FundGenius HQ while in support mode — but never bounce an explicit
      // Back-to-Platform landing off /platform/dashboard.
      if (
        isPlatformDashboardPath(location.pathname)
        || shouldRejectStaleFundGeniusRedirect(fundGeniusClientsPath, location.pathname)
      ) {
        logPortalRedirectRejected({
          candidate: fundGeniusClientsPath,
          reason: 'stale_fundgenius_workspace_after_back_to_platform',
        });
        return children;
      }
      if (!onFundGeniusHq) {
        return <Navigate to={fundGeniusClientsPath} replace />;
      }
      return children;
    }
    if (effectivePortal === 'staff_portal') {
      // Legacy: staff_portal with agency_id should land in Agency Workspace.
      if (agencyId) {
        return <Navigate to={`/agency/${agencyId}/dashboard`} replace />;
      }
      return <Navigate to="/staff/dashboard" replace />;
    }
    if (effectivePortal === 'agency' || effectivePortal === 'agency_staff') {
      if (agencyId) {
        return <Navigate to={
          supportContext?.redirect_path
          || `/agency/${agencyId}/dashboard`
        } replace />;
      }
    }
  }

  // Only platform console roles can access
  if (isClientPortalUser(user)) {
    return <Navigate to={getDashboardForUser(user)} replace />;
  }

  if (!isPlatformAdmin(user)) {
    const portal = getHostnamePortal();
    if (
      portal === HOSTNAME_PORTALS.FUNDGENIUS_APP
      && location.pathname.startsWith('/platform/fundgenius-hq')
    ) {
      return <Navigate to={FUNDGENIUS_APP_CLIENTS_PATH} replace />;
    }
    return <Navigate to={getDashboardForUser(user)} replace />;
  }

  return children;
};

const AgencyDashboardRedirect = () => {
  const { user, loading, sessionReady, authState, bootstrapState } = useAuth();
  const location = useLocation();
  const decision = resolveAgencyRouteAccess(
    user,
    { loading, sessionReady, authState, bootstrapState },
    '/agency/dashboard'
  );

  if (decision.state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-bootstrap-loading">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (decision.state === 'allow' && decision.agencyId) {
    return <Navigate to={`/agency/${decision.agencyId}/dashboard`} replace />;
  }

  const to = decision.to || '/dashboard';
  if (to === '/login') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <Navigate to={to} replace />;
};

const AgencyWorkspaceProtectedRoute = ({ children }) => {
  const { user, loading, sessionReady, authState, bootstrapState } = useAuth();
  const { agencyId } = useParams();
  const location = useLocation();
  const { isInSupportMode, supportContext, contextReady } = useSupportContext();

  // Wait for support context when actor may still be resolving Enter Agency vs client support.
  // Skip wait once /auth/me already returned support overlay (Agency or Self Repair).
  // Never wait when unauthenticated — that caused a permanent spinner after /auth/me 401.
  const awaitingSupport = Boolean(
    user
    && authState !== 'unauthenticated'
    && (user.is_platform_admin || user.platform_access || ['platform_owner', 'super_admin'].includes(String(user.role || '')))
    && !contextReady
    && !user.is_support_mode
  );

  const decision = resolveAgencyRouteAccess(
    user,
    {
      loading: loading || awaitingSupport,
      sessionReady,
      authState,
      bootstrapState,
    },
    location.pathname,
    {
      agencyId,
      isInSupportMode,
      supportContext,
    }
  );

  if (decision.state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900" data-testid="auth-bootstrap-loading">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (decision.state !== 'allow') {
    const to = decision.to || '/dashboard';
    if (to === '/login') {
      return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return <Navigate to={to} replace />;
  }

  // Never mount AgencyWorkspaceLayout for Self Repair — belt and suspenders.
  if (isCanonicalSelfRepairScope(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Never mount Agency shell while unauthenticated.
  if (!user || authState === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
};

const FundGeniusAppProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07111f]">
        <div className="animate-spin w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isClientPortalUser(user)) {
    return <Navigate to={getClientPortalDashboardPath(user)} replace />;
  }

  if (isPlatformAdmin(user) || isAgencyUser(user)) {
    return children;
  }

  return <Navigate to="/login" replace />;
};

const FundGeniusDashboardRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07111f]">
        <div className="animate-spin w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full"></div>
        <p className="sr-only">Loading your FundGenius workspace…</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Navigate
      to={getPortalLandingRoute({
        hostname: typeof window !== 'undefined' ? window.location.hostname : '',
        user,
      })}
      replace
    />
  );
};

const MarketingPublicLandingRoute = () => {
  const portal = getPortalFromHostname();
  if (portal === PORTAL_SURFACES.FUNDGENIUS_MARKETING) {
    return <FundGeniusPublicLandingPage />;
  }
  if (portal === PORTAL_SURFACES.MARKETING) {
    return <PublicLandingPage />;
  }
  return <Navigate to="/dashboard" replace />;
};

/** Root `/`: marketing domains get the public landing; app hosts stay behind auth + self-serve shell. */
const MarketingOrSelfServeRoot = () => {
  const portal = getPortalFromHostname();
  if (portal === PORTAL_SURFACES.FUNDGENIUS_MARKETING) {
    return <FundGeniusPublicLandingPage />;
  }
  if (portal === PORTAL_SURFACES.FUNDGENIUS_INTAKE) {
    return <FundingIntakePortalPage />;
  }
  if (portal === PORTAL_SURFACES.MARKETING) {
    return <PublicLandingPage />;
  }
  if (portal === PORTAL_SURFACES.FUNDGENIUS_APP) {
    return <FundGeniusDashboardRedirect />;
  }
  return (
    <ProtectedRoute>
      <PortalAwareLayout />
    </ProtectedRoute>
  );
};

/** Home index: authoritative root resolver (never first sidebar / Agencies fallback). */
const PortalHomeRedirect = () => {
  const { user, loading } = useAuth();
  const { contextReady } = useSupportContext();
  const portal = getPortalFromHostname();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F9F8]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (
    user
    && (user.role === 'platform_owner' || user.role === 'super_admin' || user.role === 'platform_admin')
    && !contextReady
    && !user.is_support_mode
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F9F8]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (portal === PORTAL_SURFACES.ADMIN_AGENCY && user) {
    return (
      <Navigate
        to={resolveAuthenticatedRootRoute({ hostname, user })}
        replace
      />
    );
  }

  if (portal === PORTAL_SURFACES.FUNDGENIUS_APP) {
    return user
      ? <Navigate to={resolveAuthenticatedRootRoute({ hostname, user })} replace />
      : <Navigate to="/login" replace />;
  }

  if (portal === PORTAL_SURFACES.SELF_REPAIR && user) {
    return <Navigate to={getDashboardForUser(user)} replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

// Admin Protected Route - For admin control center (Phase 13)
const ADMIN_ROLES = ['platform_owner', 'platform_admin', 'admin', 'owner', 'specialist'];

const AdminProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1F18]">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Only admin roles can access (exclude agency workspace users)
  if (!ADMIN_ROLES.includes(user.role) || isAgencyWorkspaceUser(user)) {
    return <Navigate to={getDashboardForUser(user)} replace />;
  }

  return children;
};

// Portal-Aware Layout - Uses appropriate layout based on portal type
const PortalAwareLayout = () => {
  const { portalType } = usePortal();
  
  // For self-serve (and staff using original routes), use the original layout
  // This preserves backward compatibility
  if (portalType === PORTAL_TYPES.STAFF) {
    // Staff can use either portal - original routes work with SelfServePortalLayout for compatibility
    return <SelfServePortalLayout />;
  }
  
  return <SelfServePortalLayout />;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();
  const portalFromHost = getPortalFromHostname();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/pricing" element={<MarketingPublicLandingRoute />} />
      <Route path="/features" element={<MarketingPublicLandingRoute />} />

      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signin" element={<Login />} />
      <Route path="/choose-account" element={<ChooseAccountPage />} />
      <Route path="/signup" element={<AuthSignupRoute />} />
      <Route path="/agency/signup" element={<AgencySignup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/confirm-login-email" element={<ConfirmLoginEmail />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/agency/dashboard" element={<AgencyDashboardRedirect />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Self-Serve Trial Signup (app.fundelitehq.ai surface) */}
      <Route path="/trial/signup" element={<SelfServeSignup />} />
      <Route path="/selfserve/signup" element={<SelfServeSignup />} />
      
      {/* Mobile Document Scanner (Public - accessed via QR code) */}
      <Route path="/mobile-scan/:sessionId" element={<MobileScanPage />} />
      <Route path="/trigger-connector-proof" element={<TriggerConnectorProofPage />} />
      <Route path="/qa-diagnostics/:runId" element={<QaDiagnosticsPage />} />

      {/* Onboarding Flow (Auto Starter ONLY) - Self-serve users only */}
      <Route
        path="/onboarding"
        element={
          <AutoStarterOnboardingRoute>
            <OnboardingFlow />
          </AutoStarterOnboardingRoute>
        }
      />

      {/* ============================================
          STAFF PORTAL ROUTES (Phase 6)
          Owner, Specialist, Staff roles
          ============================================ */}
      <Route
        path="/staff"
        element={
          <ProtectedRoute requiredPortal={PORTAL_TYPES.STAFF}>
            <StaffPortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/staff/dashboard" replace />} />
        <Route path="dashboard" element={<StaffDashboard />} />
        <Route path="clients/cases" element={<StaffCasesPage />} />
        <Route path="clients/users" element={<ClientsUsersPage />} />
        <Route path="clients/assigned" element={<StaffCasesPage />} />
        <Route path="clients/unassigned" element={<StaffCasesPage />} />
        <Route path="cases" element={<Navigate to="/staff/clients/cases" replace />} />
        <Route path="cases/assigned" element={<Navigate to="/staff/clients/assigned" replace />} />
        <Route path="cases/unassigned" element={<Navigate to="/staff/clients/unassigned" replace />} />
        <Route path="cases/:caseId" element={<StaffCaseDetailPage />} />
        <Route path="cases/:caseId/disputes" element={<StaffCaseDetailPage />} />
        <Route path="cases/:caseId/documents" element={<StaffCaseDetailPage />} />
        <Route path="cases/:caseId/uploads" element={<StaffCaseDetailPage />} />
        <Route path="queue/disputes" element={<StaffDisputeQueuePage />} />
        <Route path="queue/letters" element={<PortalPlaceholder title="Letter Queue" description="Manage pending dispute letters." backLink="/staff/dashboard" backLabel="Back to Dashboard" />} />
        <Route path="queue/document-requests" element={<StaffDocumentRequestsPage />} />
        <Route path="queue/uploads" element={<StaffUploadsQueuePage />} />
        <Route path="queue/tasks" element={<StaffTasksQueuePage />} />
        <Route path="activity" element={<StaffActivityPage />} />
        {/* Collection Letter Markup (Phase 14) */}
        <Route path="collection-markup" element={<CollectionLetterMarkupPage />} />
      </Route>

      {/* ============================================
          PLATFORM OWNER CONSOLE ROUTES (Phase 8)
          Platform owner / super admin only
          ============================================ */}
      <Route
        path="/platform"
        element={
          <PlatformOwnerProtectedRoute>
            <PlatformOwnerLayout />
          </PlatformOwnerProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/platform/dashboard" replace />} />
        <Route path="dashboard" element={<PlatformDashboard />} />
        {/* Platform Unified Inbox */}
        <Route path="unified-inbox" element={<PlatformUnifiedInboxPage />} />
        {/* Platform Opportunities (CRM Pipelines) */}
        <Route path="opportunities" element={<PlatformOpportunitiesPage />} />
        <Route path="opportunities/pipelines" element={<PlatformOpportunitiesPage />} />
        <Route path="opportunities/pipelines/:pipelineId" element={<PlatformPipelineEditPage />} />
        <Route path="agencies" element={<AgencyListPage />} />
        <Route path="agencies/:agencyId" element={<AgencyDetailPage />} />
        <Route path="agencies/:agencyId/settings" element={<AgencySettingsPage />} />
        <Route path="users" element={<PlatformUsersPage />} />
        <Route path="activity" element={<SupportActivityPage />} />
        {/* Platform Contacts (CRM) */}
        {/* Platform Contacts (CRM) */}
        <Route path="contacts" element={<PlatformContactsPage />} />
        <Route path="contacts/:contactId" element={<PlatformContactWorkspacePage />} />
        {/* Platform Tasks (CRM) */}
        <Route path="tasks" element={<PlatformTasksPage />} />
        <Route path="tasks/bulk-actions" element={<PlatformTaskBulkActionsPage />} />
        {/* Platform Automations (System Workflows) */}
        <Route path="automations" element={<PlatformAutomationsPage />} />
        <Route path="automations/workflows/:workflowId/builder" element={<PlatformWorkflowBuilderPage />} />
        <Route path="automations/template-builder" element={<PlatformAutomationsPage />} />
        {/* Redirect old plans route to settings */}
        <Route path="plans" element={<Navigate to="/platform/settings/plans" replace />} />
      </Route>

      {/* FundGenius HQ — platform admin workspace (separate shell) */}
      <Route
        path="/platform/fundgenius-hq"
        element={
          <PlatformOwnerProtectedRoute>
            <FundGeniusHQLayout />
          </PlatformOwnerProtectedRoute>
        }
      >
        <Route index element={<FundGeniusHQDashboard />} />
        <Route path="dashboard" element={<FundGeniusHQDashboard />} />
        <Route path="clients" element={<FundingClientsPage />} />
        <Route path="clients/:clientId" element={<FundingClientsPage />} />
        <Route path="profiles" element={<FundingProfilesPage />} />
        <Route path="readiness" element={<FundingReadinessPage />} />
        <Route path="ai-advisor" element={<FundingAIAdvisorPage />} />
        <Route path="documents" element={<FundingDocumentsPage />} />
        <Route path="match" element={<FundingBankMatchPage />} />
        <Route path="lenders" element={<FundingLendersPage />} />
        <Route path="lenders/:lenderId" element={<FundingLenderDetailPage />} />
        <Route path="sequence" element={<FundingSequencePage />} />
        <Route path="submissions" element={<FundingSubmissionsPage />} />
        <Route path="pipeline" element={<FundingPipelinePage />} />
        <Route path="commissions" element={<FundingCommissionsPage />} />
        <Route path="reports" element={<FundingReportsPage />} />
        <Route path="settings" element={<FundingSettingsPage />} />
      </Route>
      
      {/* ============================================
          PLATFORM SETTINGS WORKSPACE
          Configuration, integrations, and system setup
          ============================================ */}
      <Route
        path="/platform/settings"
        element={
          <PlatformOwnerProtectedRoute>
            <PlatformSettingsLayout />
          </PlatformOwnerProtectedRoute>
        }
      >
        <Route index element={<PlatformSettingsOverview />} />
        {/* General */}
        <Route path="profile" element={<PlatformProfileSettings />} />
        <Route path="branding" element={<SettingsPlaceholder title="Platform Branding" />} />
        <Route path="regional" element={<SettingsPlaceholder title="Regional Defaults" />} />
        {/* Plans & Entitlements */}
        <Route path="plans" element={<PlatformPlansPage />} />
        <Route path="plans/entitlements" element={<PlatformPlansPage />} />
        <Route path="plans/role-templates" element={<PlatformPlansPage />} />
        <Route path="plans/user-access" element={<PlatformPlansPage />} />
        <Route path="plans/agency-access" element={<PlatformPlansPage />} />
        <Route path="plans/audit" element={<PlatformPlansPage />} />
        {/* Integrations */}
        <Route path="integrations/google-address" element={<GoogleAddressSettings />} />
        <Route path="integrations/mailgun" element={<PlatformMailgunSettings />} />
        <Route path="integrations/whop" element={<PlatformWhopSettings />} />
        <Route path="integrations/postGrid" element={<PlatformPostGridSettings />} />
        <Route path="integrations/stripe" element={<SettingsPlaceholder title="Stripe" />} />
        <Route path="integrations/openai" element={<PlatformOpenAISettings />} />
        <Route path="integrations/credit-providers" element={<SettingsPlaceholder title="Credit Report Providers" />} />
        {/* Users & Access */}
        <Route path="roles" element={<SettingsPlaceholder title="Roles & Permissions" />} />
        <Route path="admin-rules" element={<SettingsPlaceholder title="Admin Access Rules" />} />
        <Route path="security-policies" element={<SettingsPlaceholder title="Security Policies" />} />
        {/* Plans Configuration */}
        <Route path="plan-defaults" element={<SettingsPlaceholder title="Plan Defaults" />} />
        <Route path="entitlement-rules" element={<SettingsPlaceholder title="Entitlement Rules" />} />
        <Route path="usage-limits" element={<SettingsPlaceholder title="Usage Limits" />} />
        <Route path="role-templates" element={<SettingsPlaceholder title="Role Templates" />} />
        {/* Communication */}
        <Route path="communication/domains" element={<PlatformDomainsSettings />} />
        <Route path="communication/system-automations" element={<Navigate to="/platform/automations" replace />} />
        <Route path="email-provider" element={<SettingsPlaceholder title="Email Provider" />} />
        <Route path="notification-templates" element={<SettingsPlaceholder title="Notification Templates" />} />
        <Route path="system-alerts" element={<SettingsPlaceholder title="System Alerts" />} />
        {/* Security */}
        <Route path="authentication" element={<AuthenticationSettingsPage />} />
        <Route path="sessions" element={<SettingsPlaceholder title="Sessions" />} />
        <Route path="audit-logs" element={<SettingsPlaceholder title="Audit Logs" />} />
        {/* Advanced */}
        <Route path="api-keys" element={<SettingsPlaceholder title="API Keys" />} />
        <Route path="webhooks" element={<SettingsPlaceholder title="Webhooks" />} />
        <Route path="feature-flags" element={<SettingsPlaceholder title="Feature Flags" />} />
        <Route path="labs" element={<SettingsPlaceholder title="Labs" />} />
        {/* Data & System */}
        <Route path="data/objects" element={<PlatformObjectsPage />} />
        <Route path="data/custom-fields" element={<PlatformCustomFieldsPage />} />
        <Route path="data/custom-values" element={<PlatformCustomValuesPage />} />
        <Route path="data/manage-scoring" element={<PlatformManageScoringPage />} />
        <Route path="data/tags" element={<PlatformTagsPage />} />
        <Route path="data/external-tracking" element={<PlatformExternalTrackingPage />} />
        <Route path="data/integrations" element={<PlatformIntegrationsHubPage />} />
        <Route path="data/private-integrations" element={<PlatformPrivateIntegrationsPage />} />
      </Route>

      {/* ============================================
          AGENCY WORKSPACE ROUTES (Agency-Scoped Context)
          Used when platform admin enters an agency context
          ============================================ */}
      <Route
        path="/agency/:agencyId"
        element={
          <AgencyWorkspaceProtectedRoute>
            <AgencyWorkspaceLayout />
          </AgencyWorkspaceProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AgencyWorkspaceDashboard />} />
        <Route path="clients/cases" element={<StaffCasesPage />} />
        <Route path="clients/users" element={<ClientsUsersPage />} />
        <Route path="clients/assigned" element={<StaffCasesPage />} />
        <Route path="clients/unassigned" element={<StaffCasesPage />} />
        <Route path="cases" element={<Navigate to="clients/cases" replace />} />
        <Route path="cases/assigned" element={<Navigate to="clients/assigned" replace />} />
        <Route path="cases/unassigned" element={<Navigate to="clients/unassigned" replace />} />
        <Route path="cases/:caseId" element={<StaffCaseDetailPage />} />
        <Route path="disputes" element={<StaffDisputeQueuePage />} />
        {/* Metro 2 Technical Dispute - Agency Full Access */}
        <Route path="disputes/metro2" element={<Metro2TechnicalWizard />} />
        <Route path="letters" element={<PortalPlaceholder title="Letter Queue" description="Manage pending dispute letters." backLink="dashboard" backLabel="Back to Dashboard" />} />
        <Route path="documents" element={<StaffDocumentRequestsPage />} />
        <Route path="uploads" element={<StaffUploadsQueuePage />} />
        <Route path="tasks" element={<AgencyTasksPage />} />
        <Route path="tasks/bulk-actions" element={<AgencyTaskBulkActionsPage />} />
        <Route path="activity" element={<StaffActivityPage />} />
        <Route path="collection-markup" element={<CollectionLetterMarkupPage />} />
        <Route path="credit-report" element={<CreditReport />} />
        <Route path="credit-report/import" element={<ImportCreditReport />} />
        <Route path="client-billing" element={<ClientBillingPage />} />
        <Route path="automations" element={<AutomationsPage />} />
        <Route path="automations/builder/:workflowId" element={<WorkflowBuilderPage />} />
        <Route path="inbox" element={<UnifiedInboxPage />} />
        <Route path="opportunities" element={<AgencyOpportunitiesPage />} />
        <Route path="contacts" element={<AgencyContactsPage />} />
        <Route path="contacts/:contactId" element={<ContactWorkspacePage />} />
        <Route path="fundgenius-hq" element={<FundGeniusHQLayout />}>
          <Route index element={<FundGeniusHQDashboard />} />
          <Route path="clients" element={<FundingClientsPage />} />
        <Route path="clients/:clientId" element={<FundingClientsPage />} />
          <Route path="profiles" element={<FundingProfilesPage />} />
          <Route path="readiness" element={<FundingReadinessPage />} />
          <Route path="ai-advisor" element={<FundingAIAdvisorPage />} />
          <Route path="documents" element={<FundingDocumentsPage />} />
          <Route path="match" element={<FundingBankMatchPage />} />
          <Route path="lenders" element={<FundingLendersPage />} />
          <Route path="lenders/:lenderId" element={<FundingLenderDetailPage />} />
          <Route path="sequence" element={<FundingSequencePage />} />
          <Route path="submissions" element={<FundingSubmissionsPage />} />
          <Route path="pipeline" element={<FundingPipelinePage />} />
          <Route path="commissions" element={<FundingCommissionsPage />} />
          <Route path="reports" element={<FundingReportsPage />} />
          <Route path="settings" element={<FundingSettingsPage />} />
        </Route>
        <Route path="settings" element={<AgencyWorkspaceSettingsPage />} />
        <Route path="settings/email-services" element={<AgencyEmailServicesPage />} />
        <Route path="settings/my-profile" element={<PortalPlaceholder title="My Profile" description="Manage your personal profile settings." backLink="settings" backLabel="Back to Settings" />} />
        <Route path="settings/security" element={<PortalPlaceholder title="Security Settings" description="Manage your security and authentication settings." backLink="settings" backLabel="Back to Settings" />} />
      </Route>

      {/* ============================================
          MANAGED CLIENT PORTAL ROUTES (Phase 6)
          Managed client role - view-focused
          ============================================ */}
      <Route
        path="/client"
        element={
          <ProtectedRoute requiredPortal={PORTAL_TYPES.MANAGED_CLIENT}>
            <ManagedClientPortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/client/dashboard" replace />} />
        <Route path="dashboard" element={<ManagedClientDashboard />} />
        <Route path="disputes" element={<ManagedClientDisputesPage />} />
        <Route path="documents" element={<ManagedClientDocumentsPage />} />
        <Route path="messages" element={<ManagedClientMessagesPage />} />
        <Route path="tasks" element={<PortalPlaceholder title="Action Items" description="Tasks that require your attention." backLink="/client/dashboard" backLabel="Back to Dashboard" />} />
        <Route path="support" element={<PortalPlaceholder title="Help & Support" description="Get help with your credit repair journey." backLink="/client/dashboard" backLabel="Back to Dashboard" />} />
      </Route>

      {/* FundGenius HQ — standalone app portal (app.fundgeniushq.com) */}
      <Route
        path="/fundgenius-hq"
        element={
          <FundGeniusAppProtectedRoute>
            <FundGeniusHQLayout />
          </FundGeniusAppProtectedRoute>
        }
      >
        <Route index element={<FundGeniusHQDashboard />} />
        <Route path="dashboard" element={<FundGeniusHQDashboard />} />
        <Route path="clients" element={<FundingClientsPage />} />
        <Route path="clients/:clientId" element={<FundingClientsPage />} />
        <Route path="profiles" element={<FundingProfilesPage />} />
        <Route path="readiness" element={<FundingReadinessPage />} />
        <Route path="ai-advisor" element={<FundingAIAdvisorPage />} />
        <Route path="documents" element={<FundingDocumentsPage />} />
        <Route path="match" element={<FundingBankMatchPage />} />
        <Route path="lenders" element={<FundingLendersPage />} />
        <Route path="lenders/:lenderId" element={<FundingLenderDetailPage />} />
        <Route path="sequence" element={<FundingSequencePage />} />
        <Route path="submissions" element={<FundingSubmissionsPage />} />
        <Route path="pipeline" element={<FundingPipelinePage />} />
        <Route path="commissions" element={<FundingCommissionsPage />} />
        <Route path="reports" element={<FundingReportsPage />} />
        <Route path="settings" element={<FundingSettingsPage />} />
      </Route>

      <Route
        path="/dashboard"
        element={
          portalFromHost === PORTAL_SURFACES.FUNDGENIUS_APP ? (
            <FundGeniusDashboardRedirect />
          ) : (
            <ProtectedRoute>
              <PortalAwareLayout />
            </ProtectedRoute>
          )
        }
      >
        <Route index element={<SelfServeDashboard />} />
      </Route>
      
      {/* ============================================
          SELF-SERVE / ORIGINAL ROUTES
          Self-repair customers + fallback
          ============================================ */}
      <Route
        path="/"
        element={<MarketingOrSelfServeRoot />}
      >
        <Route index element={<PortalHomeRedirect />} />
        
        {/* Credit Report */}
        <Route path="credit-report" element={<CreditReport />} />
        <Route path="credit-report/import" element={<ImportCreditReport />} />
        <Route path="credit-report/analysis" element={<ReportAnalysisPreview />} />
        <Route path="credit-report/analysis/print" element={<ReportAnalysisPrint />} />
        <Route path="credit-report/negative" element={<CreditReport />} />
        <Route path="credit-report/positive" element={<CreditReport />} />
        <Route path="credit-report/inquiries" element={<CreditReport />} />
        <Route path="credit-report/public-records" element={<CreditReport />} />
        <Route path="credit-report/personal" element={<CreditReport />} />
        <Route path="credit-report/addresses" element={<CreditReport />} />
        <Route path="credit-report/employment" element={<CreditReport />} />
        <Route path="credit-report/closed" element={<CreditReport />} />
        
        {/* Disputes - Metro 2 Dispute Engine Phase 4 */}
        <Route path="disputes" element={<DisputesDashboard />} />
        <Route path="my-disputes" element={<DisputesDashboard />} />
        <Route path="disputes/wizard" element={<DisputeWizardPage />} />
        <Route path="disputes/flow" element={<AutoStarterDisputeFlow />} />
        <Route path="disputes/wizard/factual" element={<FactualDisputeSelectionScreen />} />
        <Route path="disputes/wizard/metro2" element={<Metro2DisputeSelectionScreen />} />
        <Route path="disputes/wizard/review" element={<ReviewFinalizeStep />} />
        {/* Metro 2 Technical Wizard - ISOLATED from Factual flow */}
        <Route path="disputes/metro2" element={<Metro2TechnicalWizard />} />
        <Route path="disputes/pro" element={<SpecialistProWorkspace />} />
        <Route path="disputes/:roundId" element={<RoundDetailPage />} />
        <Route path="disputes/:roundId/packages/:packageId" element={<PackageDetailPage />} />
        {/* Legacy dispute routes - redirect to new dashboard */}
        <Route path="disputes/candidates" element={<DisputesDashboard />} />
        <Route path="disputes/rounds" element={<DisputesDashboard />} />
        <Route path="disputes/bureau-letters" element={<DisputesDashboard />} />
        <Route path="disputes/furnisher-letters" element={<DisputesDashboard />} />
        <Route path="disputes/ai-letters" element={<DisputesDashboard />} />
        <Route path="disputes/playbook" element={<DisputesDashboard />} />
        <Route path="disputes/letters" element={<DisputesDashboard />} />
        <Route path="disputes/tracking" element={<DisputesDashboard />} />
        <Route path="disputes/history" element={<DisputesDashboard />} />
        
        {/* Documents - Enhanced with QR scanning, crop, approval workflow */}
        <Route path="documents" element={<MyDocumentsPage />} />
        <Route path="documents/requests" element={<MyDocumentsPage />} />
        <Route path="documents/uploads" element={<MyDocumentsPage />} />
        
        {/* Collection Letter Markup (Phase 14) */}
        <Route path="collection-markup" element={<CollectionLetterMarkupPage />} />
        
        {/* Dispute Wizard (New Feature) - Fully Integrated */}
        <Route path="dispute-wizard" element={<DisputeWizardLanding />} />
        <Route path="dispute-wizard/factual" element={<DisputeWizardPage />} />
        <Route path="dispute-wizard/factual/select-items" element={<ItemSelectionPage />} />
        <Route path="dispute-wizard/factual/preview" element={<LetterPreviewPage />} />
        {/* Metro 2 Technical Wizard - ISOLATED flow (replaces shared wizard) */}
        <Route path="dispute-wizard/metro2" element={<Metro2TechnicalWizard />} />
        {/* Data Breach Method - for inquiries/personal info disputes */}
        <Route path="dispute-wizard/data_breach" element={<DisputeWizardPage />} />
        <Route path="dispute-wizard/data_breach/select-items" element={<ItemSelectionPage />} />
        <Route path="dispute-wizard/data_breach/preview" element={<LetterPreviewPage />} />
        {/* ISOLATED, STRUCTURAL, RECOVERY Methods - for account disputes */}
        <Route path="dispute-wizard/isolated" element={<DisputeWizardPage />} />
        <Route path="dispute-wizard/isolated/select-items" element={<ItemSelectionPage />} />
        <Route path="dispute-wizard/isolated/preview" element={<LetterPreviewPage />} />
        <Route path="dispute-wizard/structural" element={<DisputeWizardPage />} />
        <Route path="dispute-wizard/structural/select-items" element={<ItemSelectionPage />} />
        <Route path="dispute-wizard/structural/preview" element={<LetterPreviewPage />} />
        <Route path="dispute-wizard/recovery" element={<DisputeWizardPage />} />
        <Route path="dispute-wizard/recovery/select-items" element={<ItemSelectionPage />} />
        <Route path="dispute-wizard/recovery/preview" element={<LetterPreviewPage />} />
        <Route path="dispute-wizard/alternate-bureaus" element={<AlternateBureauFlow />} />
        <Route path="dispute-wizard/alternate-bureaus/freeze-optout" element={<AlternateBureauFlow />} />
        <Route path="dispute-wizard/alternate-bureaus/preview" element={<LetterPreviewPage />} />
        <Route path="dispute-wizard/bankruptcy-review" element={<BankruptcyReviewFlow />} />
        <Route path="dispute-wizard/bankruptcy-review/preview" element={<LetterPreviewPage />} />
        <Route path="dispute-wizard/preview" element={<LetterPreviewPage />} />
        <Route path="dispute-wizard/final-package" element={<FinalPackagePreviewPage />} />
        <Route path="dispute-wizard/step5-documents" element={<Navigate to="/dispute-wizard/final-package" replace />} />
        <Route path="dispute-wizard/step6-final-preview" element={<Step6FinalPreviewRedirect />} />
        <Route path="dispute-wizard/step5-final-preview" element={<Step6FinalPreviewRedirect />} />
        <Route path="dispute-wizard/step7-envelope" element={<DeliveryFlowStep7 />} />
        <Route path="dispute-wizard/step6-envelope" element={<Navigate to="/dispute-wizard/step7-envelope" replace />} />
        <Route path="dispute-wizard/step8-send" element={<DeliveryFlowStep8 />} />
        <Route path="dispute-wizard/states" element={<SystemStatesDemo />} />
        
        {/* Messages & Tasks */}
        <Route path="messages" element={<PortalPlaceholder title="Messages" description="Your messages and conversations." backLink="/dashboard" backLabel="Back to Dashboard" />} />
        <Route path="tasks" element={<PortalPlaceholder title="My Tasks" description="Tasks assigned to you." backLink="/dashboard" backLabel="Back to Dashboard" />} />
        
        {/* Credit Builder */}
        <Route path="credit-builder/utilization" element={<Navigate to="/credit-builder/tradelines" replace />} />
        <Route path="credit-builder/payments" element={<Navigate to="/credit-builder/tradelines" replace />} />
        <Route path="credit-builder/payment-planner" element={<PaymentPlanner />} />
        <Route path="credit-builder/cli-planner" element={<CLIPlanner />} />
        <Route path="credit-builder/credit-cards" element={<CLIPlanner />} />
        <Route path="credit-builder/simulator" element={<ScoreSimulator />} />
        <Route path="credit-builder/tradelines" element={<UtilizationManager />} />
        <Route path="credit-builder/tradeline-strategy" element={<TradelineStrategy />} />
        
        {/* Funding */}
        <Route path="funding" element={<FundingReadiness />} />
        <Route path="funding/strategy" element={<FundingReadiness />} />
        <Route path="funding/blockers" element={<FundingReadiness />} />
        <Route path="funding/bank" element={<FundingReadiness />} />
        <Route path="funding/offers" element={<FundingReadiness />} />
        
        {/* Academy */}
        <Route path="academy" element={<Academy />} />
        <Route path="academy/credit-basics" element={<Academy />} />
        <Route path="academy/dispute-strategy" element={<Academy />} />
        <Route path="academy/funding-readiness" element={<Academy />} />
        <Route path="academy/business-credit" element={<Academy />} />
        <Route path="academy/resources" element={<Academy />} />
        <Route path="academy/authorized-user-blueprint" element={<AuthorizedUserBlueprint />} />
        <Route path="academy/credit-union-blueprint" element={<CreditUnionBlueprint />} />
        <Route path="academy/rent-reporting-blueprint" element={<RentReportingBlueprint />} />
        
        {/* Blueprints - Guided Strategy Pages */}
        <Route path="blueprints/authorized-user" element={<AuthorizedUserBlueprint />} />
        <Route path="blueprints/credit-union" element={<CreditUnionBlueprint />} />
        <Route path="blueprints/secured-loan" element={<TradelineStrategy />} />
        <Route path="blueprints/rent-reporting" element={<RentReportingBlueprint />} />
        
        {/* AI Assistant */}
        <Route path="ai-assistant" element={<AIAssistant />} />
        
        {/* Billing */}
        <Route path="billing" element={<Billing />} />
        
        {/* Settings */}
        <Route path="settings" element={<SettingsHub />} />
        <Route path="settings/profile" element={<Settings />} />
        <Route path="settings/security" element={<Settings />} />
        <Route path="settings/connections" element={<Settings />} />
        <Route path="connections" element={<Navigate to="/settings/connections" replace />} />
        <Route path="settings/notifications" element={<Settings />} />
        <Route path="settings/appearance" element={<AppearanceSettings />} />
        <Route path="settings/team-activity" element={<TeamActivityPage />} />
        <Route path="settings/custom-code" element={<CustomCodeSettings />} />
        
        {/* Client Cases (staff access) */}
        <Route path="cases" element={<ClientCases />} />
        <Route path="cases/new" element={<ClientCases />} />
        <Route path="cases/active" element={<ClientCases />} />
        <Route path="cases/pending" element={<ClientCases />} />
        <Route path="cases/completed" element={<ClientCases />} />
        <Route path="cases/archived" element={<ClientCases />} />
      </Route>

      {/* ============================================
          ADMIN CONTROL CENTER ROUTES (Phase 13)
          Admin sidebar with dedicated layout
          platform_owner, platform_admin, admin, owner, specialist
          ============================================ */}
      <Route
        path="/admin"
        element={
          <AdminProtectedRoute>
            <AdminLayout />
          </AdminProtectedRoute>
        }
      >
        <Route index element={<AdminOverviewPage />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="five-s-classification" element={<FiveSClassificationPage />} />
        <Route path="training-intake" element={<TrainingIntakePage />} />
        <Route path="parser-training" element={<ParserTraining />} />
        <Route path="prompts" element={<PromptManagement />} />
        <Route path="knowledge-base" element={<KnowledgeBase />} />
        <Route path="letter-training" element={<LetterTraining />} />
        <Route path="workflow-rules" element={<WorkflowRulesPage />} />
        <Route path="methods" element={<MethodLibraryPage />} />
        <Route path="extraction-rules" element={<ExtractionRulesPage />} />
        <Route path="routing-rules" element={<RoutingRulesPage />} />
        <Route path="strategy-mapping" element={<StrategyMappingPage />} />
        <Route path="rule-testing" element={<RuleTestingPage />} />
        <Route path="templates" element={<TemplateManagerPage />} />
        <Route path="review-queue" element={<ReviewQueuePage />} />
        <Route path="sandbox" element={<SandboxTesterPage />} />
        <Route path="system-status" element={<SystemStatusPage />} />
        <Route path="audit-log" element={<AuditLogPage />} />
        <Route path="2fa-settings" element={<TwoFactorSettingsPage />} />
        <Route path="permissions" element={<PermissionsPage />} />
        <Route path="user-login-access" element={<UserLoginAccessPage />} />
        <Route path="blueprints" element={<BlueprintAdmin />} />
        {/* New Admin Architecture Pages */}
        <Route path="rules-methods" element={<RulesMethodsPage />} />
        <Route path="template-editor" element={<TemplateManagerFullPage />} />
        <Route path="testing" element={<RuleTestingPageV2 />} />
        {/* Phase 5: Decision & Letter Training Management */}
        <Route path="decision-training" element={<DecisionTrainingPage />} />
        <Route path="letter-training-mgmt" element={<LetterTrainingManagement />} />
        {/* Metro 2 Intelligence Admin Console */}
        <Route path="metro2-intelligence" element={<Metro2IntelligencePage />} />
        <Route path="data-providers/dataimpulse-provider" element={<DataImpulseProviderPage />} />
        <Route path="data-providers/911proxy-provider" element={<Proxy911ProviderPage />} />
        <Route path="data-providers/dataimpulse-location-catalog" element={<DataImpulseLocationCatalogPage />} />
        <Route path="data-providers/qa-proxy-policy" element={<QaProxyPolicyPage />} />
        <Route path="qa-website-access" element={<QaWebsiteAccessPage />} />
        {/* AI Operations (Phase 1 - Foundation) */}
        <Route path="ai-operations" element={<AIOperationsOverview />} />
        <Route path="ai-assistant" element={<AIAssistantPage />} />
        <Route path="dev-agent" element={<AICommandCenter />} />
        <Route path="ai-training" element={<AITrainingPage />} />
        <Route path="ai-usage" element={<AIUsagePage />} />
        <Route path="ai-tickets" element={<AITicketsPage />} />
      </Route>

      {/* Catch all */}
      {/* FundGenius intake portal slug routes (intake.fundgeniushq.com only) */}
      {portalFromHost === PORTAL_SURFACES.FUNDGENIUS_INTAKE ? (
        <>
          <Route path="/:agencySlug/start" element={<FundingIntakePortalPage />} />
          <Route path="/:agencySlug/client/:clientInviteId" element={<FundingIntakePortalPage />} />
          <Route path="/:agencySlug" element={<FundingIntakePortalPage />} />
        </>
      ) : null}

      <Route
        path="*"
        element={
          portalFromHost === PORTAL_SURFACES.MARKETING ||
          portalFromHost === PORTAL_SURFACES.FUNDGENIUS_MARKETING ? (
            <Navigate to="/" replace />
          ) : portalFromHost === PORTAL_SURFACES.FUNDGENIUS_INTAKE ? (
            <Navigate to="/" replace />
          ) : portalFromHost === PORTAL_SURFACES.FUNDGENIUS_APP ? (
            <Navigate to="/login" replace />
          ) : portalFromHost === PORTAL_SURFACES.ADMIN_AGENCY ? (
            <Navigate to="/platform/dashboard" replace />
          ) : (
            <Navigate to="/dashboard" replace />
          )
        }
      />
    </Routes>
  );
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <I18nProvider>
          <AuthProvider>
            <EntitlementProvider>
              <SupportContextProvider>
                <PortalProvider>
                  <SupportWidgetProvider>
                    <OnboardingProvider>
                      <ReportDataProvider>
                        <DisputeTrayProvider>
                          <HostnameRouteGate>
                            <AppRouter />
                            <DisputeTray />
                            <BulkDisputeWizard />
                            <ConsistencyDebugPanel />
                            <SupportWidget />
                            <Toaster position="top-right" richColors />
                          </HostnameRouteGate>
                        </DisputeTrayProvider>
                      </ReportDataProvider>
                    </OnboardingProvider>
                  </SupportWidgetProvider>
                </PortalProvider>
              </SupportContextProvider>
            </EntitlementProvider>
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
